import React, { FC } from 'react';

import { SerializedStyles } from '@emotion/react';
import { alpha, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import PlaySmallIcon from '../../../../assets/icons/PlaySmallIcon';
import RepeatIcon from '../../../../assets/icons/RepeatIcon';
import { calculatePathwayStatus, PATHWAY_ACTIONS } from '../../../../pages/DiscoverPage/const';
import styled from '../../../../styled';
import { colorsPalette, SMALL_MARGIN_PX } from '../../../../theme';
import { CustomButton } from '../../../UI/CustomButton';
import CardTestResults from '../CardTestResults/CardTestResults';

interface CardPathwayActionBlockProps {
  pathwaySlug?: string | null;
  testId?: number | null;
  hash?: string | null;
  totalVideos: number;
  watchedVideos: number;
  passDate?: string | null;
  pathwayName?: string | null;
  tenantTitle?: string | null;
  tenantName?: string | null;
  wrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  cpdCredits?: string | null;
  category?: string | null;
}

const CardPathwayActionBlock: FC<CardPathwayActionBlockProps> = ({
  pathwaySlug,
  testId,
  hash,
  totalVideos,
  watchedVideos,
  passDate,
  pathwayName,
  tenantTitle,
  tenantName,
  wrapperCss,
  cpdCredits,
  category,
}) => {
  const { t } = useTranslation();
  const status = calculatePathwayStatus(watchedVideos, totalVideos, hash, testId);

  return (
    <Wrapper optionalStyles={wrapperCss}>
      {status === PATHWAY_ACTIONS.WATCH_NEW_CONTENT || status === PATHWAY_ACTIONS.PASSED_TEST ? (
        <CardTestResults
          pathwayName={pathwayName}
          passDate={passDate}
          hash={hash}
          tenantTitle={tenantTitle}
          tenantName={tenantName}
          cpdCredits={cpdCredits}
          category={category}
        />
      ) : (
        <div />
      )}
      {status === PATHWAY_ACTIONS.CONTINUE_WATCHING && (
        <Link data-testid="pathwayLink" to={`/pathway/${pathwaySlug}`}>
          <StyledButton data-testid="continueWatchingBtn">
            <StyledPlaySmallIcon data-testid="play-icon" className="playIcon" />
            {t('page.discover.continueWatching')}
          </StyledButton>
        </Link>
      )}
      {status === PATHWAY_ACTIONS.TAKE_TEST && (
        <Link data-testid="pathwayLink" to={`/pathway/${pathwaySlug}/test/${testId}`}>
          <CustomButton data-testid="takeTest">{t('page.discover.takeTest')}</CustomButton>
        </Link>
      )}
      {status === PATHWAY_ACTIONS.WATCH_NEW_CONTENT && (
        <Link data-testid="pathwayLink" to={`/pathway/${pathwaySlug}`}>
          <StyledButton data-testid="watchNewContent">
            <StyledPlaySmallIcon data-testid="play-icon" className="playIcon" />
            {t('page.discover.watchNewContent')}
          </StyledButton>
        </Link>
      )}
      {(status === PATHWAY_ACTIONS.PASSED_TEST || status === PATHWAY_ACTIONS.WATCH_AGAIN) && (
        <Link data-testid="pathwayLink" to={`/pathway/${pathwaySlug}`}>
          <StyledButton data-testid="rewatch">
            <StyledRepeatIcon data-testid="repeat-icon" className="repeatIcon" />
            {t('page.discover.rewatch')}
          </StyledButton>
        </Link>
      )}
    </Wrapper>
  );
};

export default CardPathwayActionBlock;

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${SMALL_MARGIN_PX};
  padding: 24px;
  background-color: ${colorsPalette.violetAnalyticsCard};
  flex-direction: column;
  border-radius: 5px;
  box-shadow: 0px 3px 5px ${colorsPalette.black};
  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
  }
  ${({ optionalStyles }) => optionalStyles}
`;
const StyledButton = styled(CustomButton)`
  &:hover .repeatIcon {
    color: ${alpha(colorsPalette.black, 0.87)};
  }
  &:hover .playIcon path {
    stroke: ${alpha(colorsPalette.black, 0.87)};
  }
`;
const createStyledIcon = (tag) => styled(tag)`
  width: 14px;
  height: 14px;
  margin-right: 9px;
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`;
const StyledPlaySmallIcon = createStyledIcon(PlaySmallIcon);
const StyledRepeatIcon = createStyledIcon(RepeatIcon);
